import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Loader2, CalendarCheck, Users
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { getRequestTypeLabel } from '@/lib/attendanceTypes';
import { formatLocalDateString } from '@/lib/dateUtils';

// Leave type colors
const getLeaveTypeColor = (type) => {
  const colors = {
    'annual_leave': 'bg-blue-100 text-blue-800 border-blue-200',
    'sick_leave': 'bg-red-100 text-red-800 border-red-200',
    'unpaid_leave': 'bg-gray-100 text-gray-800 border-gray-200',
    'work_from_home': 'bg-purple-100 text-purple-800 border-purple-200',
    'compassionate_leave': 'bg-orange-100 text-orange-800 border-orange-200',
    'parental_leave': 'bg-pink-100 text-pink-800 border-pink-200',
    'training': 'bg-green-100 text-green-800 border-green-200'
  };
  return colors[type] || colors['annual_leave'];
};

const formatDate = (dateStr) => {
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short'
  });
};

const formatDateRange = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (start.toDateString() === end.toDateString()) {
    return formatDate(startDate);
  }
  return `${formatDate(startDate)} - ${formatDate(endDate)}`;
};

const getDaysUntil = (dateStr) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const date = new Date(dateStr);
  date.setHours(0, 0, 0, 0);
  const diffTime = date - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

const getDaysUntilLabel = (days) => {
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  return `In ${days} days`;
};

export function UpcomingLeaveTile({ filter, size, useMockData }) {
  const navigate = useNavigate();
  const { company } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!company?.id) {
      setLoading(false);
      return;
    }

    const loadUpcomingLeave = async () => {
      try {
        setLoading(true);

        // Get all company user IDs
        const { data: companyUsers } = await supabase
          .from('user_profiles')
          .select('id')
          .eq('company_id', company.id);

        const companyUserIds = (companyUsers || []).map(u => u.id);

        if (companyUserIds.length === 0) {
          setRequests([]);
          setLoading(false);
          return;
        }

        // Get today's date and 30 days from now
        const today = formatLocalDateString(new Date());
        const futureDate = new Date();
        futureDate.setDate(futureDate.getDate() + 30);
        const thirtyDaysLater = formatLocalDateString(futureDate);

        // Load approved leave requests starting in the next 30 days
        const { data: requestsData, error: requestsError } = await supabase
          .from('leave_requests')
          .select(`
            *,
            user_profiles!leave_requests_user_id_fkey(
              first_name,
              last_name,
              email
            )
          `)
          .in('user_id', companyUserIds)
          .eq('status', 'approved')
          .gte('start_date', today)
          .lte('start_date', thirtyDaysLater)
          .order('start_date', { ascending: true })
          .limit(10);

        if (requestsError) throw requestsError;

        setRequests(requestsData || []);
      } catch (error) {
        console.error('Failed to load upcoming leave:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUpcomingLeave();
  }, [company?.id]);

  const goToCalendar = () => {
    navigate('/attendance/my-calendar');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-6 w-6 animate-spin text-violet-600" />
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-4">
        <div className="bg-gray-100 p-3 rounded-full mb-3">
          <Users className="h-6 w-6 text-gray-400" />
        </div>
        <p className="text-sm font-medium text-gray-900">No upcoming leave</p>
        <p className="text-xs text-gray-500 mt-1">Team is fully available</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between mb-2 pb-2 border-b shrink-0">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
            Next 30 days
          </Badge>
          <span className="text-xs text-gray-600">{requests.length} scheduled</span>
        </div>
      </div>

      {/* Requests list */}
      <ScrollArea className="flex-1 min-h-0">
        <div className="space-y-2 pr-2">
          {requests.map((request) => {
            const daysUntil = getDaysUntil(request.start_date);
            const isStartingSoon = daysUntil <= 3;

            return (
              <div
                key={request.id}
                onClick={goToCalendar}
                className={cn(
                  "p-2 rounded-lg border",
                  isStartingSoon ? "bg-blue-50 border-blue-200" : "bg-gray-50 border-gray-200",
                  "hover:shadow-md cursor-pointer transition-all"
                )}
              >
                <div className="flex items-start gap-2">
                  {/* Avatar */}
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-semibold text-[10px]">
                      {request.user_profiles?.first_name?.[0]}{request.user_profiles?.last_name?.[0]}
                    </span>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className="text-xs font-medium text-gray-900 truncate">
                        {request.user_profiles?.first_name} {request.user_profiles?.last_name}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Badge className={cn("text-[10px] px-1.5 py-0", getLeaveTypeColor(request.leave_type))}>
                        {getRequestTypeLabel(request.leave_type)}
                      </Badge>
                      <span className="text-[10px] text-gray-600">
                        {formatDateRange(request.start_date, request.end_date)}
                      </span>
                    </div>
                  </div>

                  {/* Days until badge */}
                  <Badge
                    variant="outline"
                    className={cn(
                      "text-[10px] shrink-0",
                      isStartingSoon
                        ? "bg-blue-100 text-blue-700 border-blue-300"
                        : "bg-gray-100 text-gray-600 border-gray-300"
                    )}
                  >
                    {getDaysUntilLabel(daysUntil)}
                  </Badge>
                </div>
              </div>
            );
          })}
        </div>
      </ScrollArea>

      {/* Footer - link to calendar */}
      <button
        onClick={goToCalendar}
        className="mt-2 pt-2 border-t flex items-center justify-center gap-1 text-xs text-violet-600 hover:text-violet-800 font-medium transition-colors shrink-0"
      >
        <CalendarCheck className="h-3.5 w-3.5" />
        <span>View Team Calendar</span>
      </button>
    </div>
  );
}
